import React, { type CSSProperties } from 'react';
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useDraggable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import type { CrmOportunidadListItem } from '../../../types/crm';
import type { CrmRealtimeFuente } from '../../../types/crmRealtime';
import { formatCurrency } from '../../../utils/formatters';

export interface CrmDealCardProps {
  deal: CrmOportunidadListItem;
  onClick?: () => void;
  dragDisabled?: boolean;
  isNew?: boolean;
  fuente?: CrmRealtimeFuente | string | null;
  pipelineLabel?: string | null;
}

const CrmDealCard: React.FC<CrmDealCardProps> = ({
  deal,
  onClick,
  dragDisabled = false,
  isNew = false,
  fuente = null,
  pipelineLabel = null,
}) => {
  const isClosed =
    (deal.estado || '').toLowerCase().includes('ganad') ||
    (deal.estado || '').toLowerCase() === 'perdida';
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `deal-${deal.id}`,
    data: { deal },
    disabled: dragDisabled || isClosed,
  });

  const style: CSSProperties = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.5 : 1,
    cursor: dragDisabled || isClosed ? 'pointer' : 'grab',
  };

  const prob = deal.probabilidad != null ? Math.max(0, Math.min(100, Number(deal.probabilidad))) : null;
  const isAgent = fuente === 'agente';
  const isWon = (deal.estado || '').toLowerCase().includes('ganad');
  const isLost = (deal.estado || '').toLowerCase() === 'perdida';

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: '#fff',
        borderRadius: 2,
        border: '1px solid',
        borderColor: isNew
          ? 'primary.main'
          : isWon
            ? 'success.main'
            : isLost
              ? 'error.light'
              : deal.estancada
                ? 'warning.main'
                : 'divider',
        opacity: isLost ? 0.85 : 1,
        boxShadow: isNew
          ? '0 0 0 2px rgba(25,118,210,0.25), 0 4px 16px rgba(25,118,210,0.2)'
          : '0 1px 3px rgba(0,0,0,0.08)',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease, transform 0.35s ease',
        animation: isNew ? 'crmDealEnter 0.45s ease-out' : undefined,
        '@keyframes crmDealEnter': {
          from: { opacity: 0, transform: 'translateY(-10px) scale(0.96)' },
          to: { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
        '&:hover': { boxShadow: '0 2px 10px rgba(0,0,0,0.14)' },
        ...(isNew && {
          '@keyframes crmNewPulse': {
            '0%, 100%': { boxShadow: '0 0 0 2px rgba(25,118,210,0.25), 0 4px 16px rgba(25,118,210,0.2)' },
            '50%': { boxShadow: '0 0 0 4px rgba(25,118,210,0.35), 0 6px 20px rgba(25,118,210,0.28)' },
          },
          animation: 'crmDealEnter 0.45s ease-out, crmNewPulse 1.6s ease-in-out 3',
        }),
      }}
    >
      <Stack spacing={0.75}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.75}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, flex: 1 }}>
            {deal.nombre}
          </Typography>
          <Stack direction="row" spacing={0.5} flexShrink={0} flexWrap="wrap" useFlexGap justifyContent="flex-end">
            {isNew && <Chip size="small" label="NUEVA" color="primary" sx={{ height: 20, fontWeight: 700, fontSize: 10 }} />}
            {isWon && (
              <Chip size="small" label="Ganada" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
            )}
            {isLost && (
              <Chip size="small" label="Perdida" color="error" variant="outlined" sx={{ height: 20, fontSize: 10 }} />
            )}
            {isAgent && (
              <Chip
                size="small"
                icon={<SmartToyIcon sx={{ fontSize: '14px !important' }} />}
                label="Agente IA"
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: 'rgba(0,137,123,0.12)',
                  color: '#00897b',
                  '& .MuiChip-icon': { color: '#00897b' },
                }}
              />
            )}
            {deal.estancada && <Chip size="small" label="Estancada" color="warning" sx={{ height: 20, fontSize: 10 }} />}
          </Stack>
        </Stack>
        <Typography variant="body2" sx={{ fontWeight: 700, color: '#1976d2', fontSize: '0.95rem' }}>
          {formatCurrency(deal.monto)}
        </Typography>
        {pipelineLabel && (
          <Chip
            size="small"
            label={pipelineLabel}
            variant="outlined"
            sx={{ height: 20, fontSize: 10, alignSelf: 'flex-start', maxWidth: '100%' }}
          />
        )}
        {deal.contactoPrincipal && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {deal.contactoPrincipal}
          </Typography>
        )}
        {prob != null && (
          <Box>
            <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.25 }}>
              <Typography variant="caption" color="text.secondary">
                Probabilidad
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: prob >= 70 ? '#2e7d32' : prob >= 40 ? '#ed6c02' : '#d32f2f' }}>
                {prob}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={prob}
              sx={{
                height: 4,
                borderRadius: 1,
                bgcolor: 'rgba(0,0,0,0.06)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: prob >= 70 ? '#2e7d32' : prob >= 40 ? '#ed6c02' : '#d32f2f',
                  borderRadius: 1,
                },
              }}
            />
          </Box>
        )}
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '55%' }}>
            {deal.nombreVendedor || `Vend. ${deal.nroVendedor}`}
          </Typography>
          <Stack direction="row" spacing={0.75} alignItems="center">
            {(deal.cantidadAdjuntos ?? 0) > 0 && (
              <Stack direction="row" spacing={0.25} alignItems="center" title={`${deal.cantidadAdjuntos} adjunto(s)`}>
                <AttachFileIcon sx={{ fontSize: 14, color: 'primary.main' }} />
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {deal.cantidadAdjuntos}
                </Typography>
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {deal.diasEnEtapa}d
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default CrmDealCard;
